import { useState } from 'react';
import { Loader2, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateContactoRequest } from '@visep/shared-types/presupuestos';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/use-form-submit';
import { glassInput, glassLabel } from '@/components/landing/glass';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface ContactForm {
  nombre: string;
  telefono: string;
  email: string;
  mensaje: string;
}

const EMPTY: ContactForm = { nombre: '', telefono: '', email: '', mensaje: '' };

export function Contact() {
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const [honeypot, setHoneypot] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { status, errorMessage, submit } = useFormSubmit((input: CreateContactoRequest) =>
    api.contactos.create(input),
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!form.nombre.trim() || !form.email.trim() || !form.mensaje.trim()) {
      setLocalError('Completá nombre, correo y mensaje.');
      return;
    }
    const input: CreateContactoRequest = {
      nombre: form.nombre.trim(),
      email: form.email.trim(),
      mensaje: form.mensaje.trim(),
      ...(form.telefono.trim() ? { telefono: form.telefono.trim() } : {}),
    };
    const ok = await submit(input, honeypot);
    if (ok) {
      toast.success(
        'Mensaje enviado. Hemos recibido tu consulta y te contactaremos a la brevedad.',
      );
      setForm(EMPTY);
    }
  }

  return (
    <section
      id="contact"
      className="relative scroll-mt-16 overflow-hidden border-t border-white/[0.04] py-20 lg:py-32"
    >
      <div className="container relative z-10 grid gap-16 lg:grid-cols-2 lg:items-start">
        <div>
          <h2 className="text-4xl font-bold tracking-tight text-white lg:text-5xl">Contáctenos</h2>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-slate-400">
            Estamos listos para asesorarle. Nuestros expertos en seguridad analizarán sus
            necesidades específicas.
          </p>
          <div className="mt-10 space-y-8">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Phone className="h-6 w-6 text-primary" aria-hidden />
              </span>
              <div>
                <h3 className="font-semibold text-white">Llámenos</h3>
                <p className="text-slate-400">+595 21 123 456</p>
                <p className="text-sm text-slate-400">Atención 24/7</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <MapPin className="h-6 w-6 text-primary" aria-hidden />
              </span>
              <div>
                <h3 className="font-semibold text-white">Visítenos</h3>
                <p className="text-slate-400">Av. Mcal. López esq. Gral. Santos</p>
                <p className="text-sm text-slate-400">Asunción, Paraguay</p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8"
        >
          <div className="grid gap-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="contact-nombre" className={glassLabel}>Nombre</Label>
                <Input
                  id="contact-nombre"
                  className={glassInput}
                  name="nombre"
                  autoComplete="name"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact-telefono" className={glassLabel}>Teléfono</Label>
                <Input
                  id="contact-telefono"
                  className={glassInput}
                  name="telefono"
                  type="tel"
                  autoComplete="tel"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-email" className={glassLabel}>Email</Label>
              <Input
                id="contact-email"
                className={glassInput}
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-mensaje" className={glassLabel}>Mensaje</Label>
              <Textarea
                id="contact-mensaje"
                className={glassInput}
                name="mensaje"
                value={form.mensaje}
                onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                required
              />
            </div>
            {/* Honeypot anti-spam */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
            {(localError || errorMessage) && (
              <p className="text-sm text-error" role="alert">
                {localError ?? errorMessage}
              </p>
            )}
            <Button type="submit" size="lg" disabled={status === 'loading'}>
              {status === 'loading' ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Enviando…
                </>
              ) : (
                'Enviar Mensaje'
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
